import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';
import { X, Pen } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useUserFormMutations } from './useUserFormMutations';
import { UserFormFields } from './UserFormFields';
import { useState } from 'react';

const getSchema = (isEdit: boolean, t: TFunction) =>
  z.object({
    phone: z
      .string()
      .min(1, t('phoneRequired'))
      .regex(/^\+998 \(\d{2}\) \d{3} \d{2} \d{2}$/, t('invalidPhoneFormat')),
    name: z.string().min(1, t('nameRequired')),
    password: isEdit
      ? z.string().optional()
      : z.string().min(8, t('passwordRequired')),
    email: z.string().email(t('invalidEmail')).or(z.literal('')).optional(),
    status: z.enum(['ACTIVE', 'BLOCKED']),
    roleId: z.number({ required_error: t('roleRequired') }),
    hiredAt: z.date({ required_error: t('dateRequired') }).optional(),
    salary: z
      .number({ invalid_type_error: t('invalidNumber') })
      .nonnegative()
      .optional(),
    // The KPI hours norm is agreed per MONTH (client review AL-2026/08-04-01,
    // K-05); a period shorter than a month is prorated by working days.
    monthlyExpectedHours: z
      .number({ invalid_type_error: t('invalidNumber') })
      .int()
      .min(1)
      .max(744)
      .optional(),
  });

type FormValues = z.infer<ReturnType<typeof getSchema>>;

interface CreateUserDialogProps {
  id?: number | string;
}

export function CreateUserDialog({ id }: CreateUserDialogProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const isEdit = !!id;

  const schema = useMemo(() => getSchema(isEdit, t), [isEdit, t]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      phone: '',
      name: '',
      email: '',
      status: 'ACTIVE',
      roleId: undefined,
      hiredAt: undefined,
      salary: undefined,
      monthlyExpectedHours: undefined,
    },
  });

  const { fetchUser, createUser, updateUser, isPending } = useUserFormMutations({
    id,
    onCreateSuccess: () => {
      form.reset();
      setOpen(false);
    },
    onUpdateSuccess: () => {
      form.reset();
      setOpen(false);
    },
  });

  useEffect(() => {
    if (open && id) {
      fetchUser(id, {
        onSuccess: (data) => {
          form.reset({
            phone: data.phone.slice(3),
            name: data.name,
            email: data.email ?? '',
            status: data.status,
            roleId: data.roleId,
            hiredAt: data.hiredAt ? new Date(data.hiredAt) : undefined,
            salary: data.salary ?? undefined,
            monthlyExpectedHours: data?.monthlyExpectedHours ?? undefined,
          });
        },
        onError: (err: { message?: string }) => {
          toast.error(t('loadError', { error: err?.message || 'Unknown error' }));
        },
      });
    }
  }, [open, id, fetchUser]);

  // The API stores the monthly norm in hours, so the form value goes straight through.
  const toPayload = (values: FormValues) => ({ ...values });

  const onSubmit = (values: FormValues) => {
    const payload = toPayload(values);
    if (isEdit) {
      if (!payload.password) {
        delete payload.password;
      }
      updateUser({ userId: id!, data: payload });
    } else {
      createUser(payload);
    }
  };

  const handleSaveAndCreate = (values: FormValues) => {
    createUser(toPayload(values), {
      onSuccess: () => {
        toast.success(t('createdSuccess', { entity: t('user') }));
        form.reset();
      },
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {isEdit ? (
          <Button size="sm" variant="outline" className="rounded-r-none">
            <Pen className="h-4 w-4" />
          </Button>
        ) : (
          <Button>
            {t('new')} {t('userya')}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent className="p-6 min-w-xl max-h-[80%] overflow-scroll">
        <div className="flex items-center justify-between border-b pb-4 border-[#D9D9D9] w-full">
          <AlertDialogTitle className="text-xl font-semibold">
            {isEdit ? t('update') : t('new')} {t('userya')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => setOpen(false)} aria-label="Close dialog">
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <UserFormFields
          // eslint_disable-next-line @typescript-eslint/no-explicit-any
          form={form as any}
          isEdit={isEdit}
          onSubmit={onSubmit}
        />

        <AlertDialogFooter className="flex justify-end gap-2 mt-4">
          <AlertDialogCancel onClick={() => setOpen(false)}>
            {t('cancel')}
          </AlertDialogCancel>
          {isEdit ? (
            <Button
              type="button"
              disabled={isPending}
              onClick={form.handleSubmit(onSubmit)}
            >
              {isPending ? t('saving') : t('save')}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={form.handleSubmit(handleSaveAndCreate)}
              >
                {isPending ? t('saving') : t('saveAndCreate')}
              </Button>
              <Button
                type="button"
                disabled={isPending}
                onClick={form.handleSubmit(onSubmit)}
              >
                {isPending ? t('saving') : t('save')}
              </Button>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
